// -----------------------------------------------------------------------------
// src/pages/AccountManagementDashboard.jsx
// Revamped 2025‑07‑08 – sleeker glass‑morphism header, ultra‑responsive tabs, and
// full mobile accessibility (no more cut‑off options). Copy‑paste ready.
// -----------------------------------------------------------------------------
import React, { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Typography,
  Container,
  Box,
  Tabs,
  Tab,
  TextField,
  Fab,
  IconButton,
  Drawer,
  useTheme,
  useMediaQuery,
  InputAdornment,
  alpha,
} from "@mui/material";
import {
  Search as SearchIcon,
  Groups,
  Campaign,
  Person,
  Contacts as ContactsIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import CompaniesView from "./pages/companies";
import CreatorsView from "./pages/creators";
import ClientsView from "./pages/clients";
import CampaignsView from "./pages/campaigns";
import ContactDashboard from "../../CreatorUser/Contacts/contactdash";

/* ───────── Tab Definitions ───────── */
const tabList = [
  { key: "clients", label: "Clients", icon: <Person fontSize="small" />, view: ClientsView },
  { key: "campaigns", label: "Campaigns", icon: <Campaign fontSize="small" />, view: CampaignsView },
  { key: "creators", label: "Creators", icon: <Person fontSize="small" />, view: CreatorsView },
  { key: "companies", label: "Companies", icon: <Groups fontSize="small" />, view: CompaniesView },
];

export default function AccountManagementDashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const theme   = useTheme();
  const isXs    = useMediaQuery(theme.breakpoints.down("sm"));
  const isSmUp  = useMediaQuery(theme.breakpoints.up("sm"));
  const isMdUp  = useMediaQuery(theme.breakpoints.up("md"));

  /* ——— State ——— */
  const initialTab = useMemo(() => searchParams.get("tab") || "clients", [searchParams]);
  const [activeTab, setActiveTab]           = useState(initialTab);
  const [searchQuery, setSearchQuery]       = useState("");
  const [contactsOpen, setContactsOpen]     = useState(false);

  const ActiveView = tabList.find((t) => t.key === activeTab).view;

  /* ───────────────────────────────────────────────────────────────────────── */
  return (
    <>
      {/* ─────────── Sticky Glass Header ─────────── */}
      <AppBar
        position="sticky"
        elevation={0}
        color="transparent"
        sx={{
          backdropFilter: "blur(10px)",
          backgroundColor: alpha(theme.palette.background.paper, 0.85),
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Toolbar
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            gap: 2,
            py: { xs: 1, sm: 0 },
          }}
        >
          {/* Title */}
          <Typography variant="h6" fontWeight={800} sx={{ flexShrink: 0 }}>
            Account&nbsp;Management
          </Typography>

          {/* Tabs */}
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Tabs
              value={activeTab}
              onChange={(_, v) => {
                setActiveTab(v);
                setSearchQuery("");
                setSearchParams({ tab: v });
              }}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              textColor="primary"
              indicatorColor="primary"
              sx={{
                minHeight: 40,
                ".MuiTabs-scrollButtons": {
                  display: "flex !important", // ensure buttons always visible on mobile
                },
                ".MuiTab-root": {
                  minHeight: 40,
                  minWidth: { xs: 80, sm: 120 },
                  px: { xs: 1.25, sm: 2 },
                  gap: 0.5,
                },
                ".MuiTabs-indicator": {
                  height: 3,
                  borderRadius: 2,
                },
              }}
            >
              {tabList.map((t) => (
                <Tab
                  key={t.key}
                  value={t.key}
                  icon={t.icon}
                  iconPosition="start"
                  label={isMdUp ? t.label : undefined}
                />
              ))}
            </Tabs>
          </Box>

          {/* Search */}
          <Box sx={{ flexShrink: 0, width: { xs: "100%", sm: 260 } }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{
                backdropFilter: "blur(6px)",
                backgroundColor: alpha(theme.palette.background.default, 0.8),
                borderRadius: 2,
              }}
            />
          </Box>
        </Toolbar>
      </AppBar>

      {/* ─────────── Content ─────────── */}
      <Container
        maxWidth="xl"
        sx={{
          pt: { xs: 6, sm: 4, md: 6 },
          pb: { xs: 3, md: 4 },
          px: { xs: 1, sm: 2, md: 3 },
          minHeight: "60vh",
        }}
      >
        <ActiveView
          navigate={navigate}
          searchQuery={searchQuery}
          renderIcon={require("./dashboardUtils").renderIcon}
          renderAvatar={require("./dashboardUtils").renderAvatar}
        />
      </Container>

      {/* ─────────── Floating Contacts Button ─────────── */}
      <Fab
        color="primary"
        aria-label="contacts"
        onClick={() => setContactsOpen(true)}
        size={isSmUp ? "medium" : "small"}
        sx={{
          position: "fixed",
          bottom: { xs: 16, sm: 24 },
          right: { xs: 16, sm: 24 },
          zIndex: (t) => t.zIndex.drawer + 1,
          boxShadow: 6,
        }}
      >
        <ContactsIcon />
      </Fab>

      {/* ─────────── Contacts Drawer ─────────── */}
      <Drawer
        anchor="right"
        open={contactsOpen}
        onClose={() => setContactsOpen(false)}
        keepMounted
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 420, md: 560 },
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1.5,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Typography variant="subtitle1" fontWeight={700}>
            Contact&nbsp;Manager
          </Typography>
          <IconButton onClick={() => setContactsOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ flex: 1, overflow: "auto" }}>
          <ContactDashboard />
        </Box>
      </Drawer>
    </>
  );
}
